// Orders and groups jobs for the job picker in Expense Management so the list fits the expense.
// Nothing is hidden: jobs are only reordered and grouped (finished jobs sit in a collapsed group).
//
// The "active period" rule mirrors api/_matching.js (ACTIVE_MARGIN_DAYS, addToPeriods, jobsActiveOn):
// a job's period runs from its earliest to its latest dated transaction (revenue and costs), plus 14
// days on each side. api/ cannot be imported into the React build, so the rule is repeated here.
// If you change it there, change it here.
// One difference: the server ignores rows Canopy wrote from an automatic guess (ids with "_automatch_")
// when it builds active periods; the picker may count them, so its "Running" group can be slightly wider.

export const ACTIVE_MARGIN_DAYS = 14;

// "2026-03-05" (or a longer ISO timestamp) -> whole day number, or null. UTC math, so time zones never shift it.
export function dayNumber(value) {
  if (typeof value !== 'string') return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!m) return null;
  const ms = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(ms) ? null : Math.floor(ms / 86400000);
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Day number -> "May 8"
export function dayLabel(day) {
  const d = new Date(day * 86400000);
  return `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}`;
}

// Earliest and latest dated activity of a job summary (invoices and purchases), as day numbers.
// Returns { start, end } or null when the job has no dated activity.
export function jobActivity(job) {
  let start = null, end = null;
  const add = (v) => {
    const d = dayNumber(v);
    if (d === null) return;
    if (start === null || d < start) start = d;
    if (end === null || d > end) end = d;
  };
  ((job && job.invoices) || []).forEach(t => add(t && t.TxnDate));
  ((job && job.purchases) || []).forEach(t => add(t && t.TxnDate));
  return start === null ? null : { start, end };
}

// Job summaries -> the lightweight list the picker uses.
export function toPickerJobs(jobSummaries) {
  return (jobSummaries || []).map(j => {
    const a = jobActivity(j);
    return {
      value: j.id, label: j.name || '', client: j.clientName || '',
      finished: j.status === 'Complete',
      start: a ? a.start : null, end: a ? a.end : null,
    };
  });
}

// The day range an expense (or a set of expenses) covers: { from, to } in day numbers, or null when
// nothing has a usable date. Accepts a date string or an array of date strings.
export function expenseRange(dates) {
  const list = Array.isArray(dates) ? dates : [dates];
  let from = null, to = null;
  list.forEach(v => {
    const d = dayNumber(v);
    if (d === null) return;
    if (from === null || d < from) from = d;
    if (to === null || d > to) to = d;
  });
  return from === null ? null : { from, to };
}

// Whole days between a range and a job's raw (un-widened) period; 0 when they overlap.
function gapDays(range, job) {
  if (range.to < job.start) return job.start - range.to;
  if (range.from > job.end) return range.from - job.end;
  return 0;
}

// Is the job running in the range? Its period, widened by 14 days each side, overlaps the range.
export function isRunning(job, range) {
  if (!range || job.start === null || job.start === undefined) return false;
  return gapDays(range, job) <= ACTIVE_MARGIN_DAYS;
}

// "Running around May 8" for one day, "Running May 8 - Jun 2" for a range.
export function runningLabel(range) {
  if (range.from === range.to) return `Running around ${dayLabel(range.from)}`;
  return `Running ${dayLabel(range.from)} - ${dayLabel(range.to)}`;
}

const byName = (a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });

// Groups jobs for the picker.
//   jobs:  from toPickerJobs
//   range: from expenseRange, or null/undefined when the expense has no date (then no "running" group)
//   query: search text; matches job name or client name, case-insensitive, across ALL jobs
// Returns [{ key: 'running' | 'open' | 'finished', label, jobs }], leaving out empty groups.
// Order: running (closest to the date first, then name), other open jobs, finished jobs.
export function groupJobsForPicker(jobs, range, query) {
  const q = String(query || '').trim().toLowerCase();
  const matches = (j) => !q || j.label.toLowerCase().includes(q) || (j.client || '').toLowerCase().includes(q);
  const running = [], open = [], finished = [];
  (jobs || []).filter(matches).forEach(j => {
    if (range && isRunning(j, range)) running.push(j);
    else if (!j.finished) open.push(j);
    else finished.push(j);
  });
  running.sort((a, b) => gapDays(range, a) - gapDays(range, b) || byName(a, b));
  open.sort(byName);
  finished.sort(byName);
  const groups = [];
  if (running.length) groups.push({ key: 'running', label: runningLabel(range), jobs: running });
  if (open.length) groups.push({ key: 'open', label: 'Other open jobs', jobs: open });
  if (finished.length) groups.push({ key: 'finished', label: 'Finished jobs', jobs: finished });
  return groups;
}
